import { Card } from "@/components/ui/card";
import { FileVideo } from "lucide-react";

interface SimpleTotalVideosCardProps {
  publishedCount: number;
  logo?: string;
}

export const SimpleTotalVideosCard = ({
  publishedCount,
  logo,
}: SimpleTotalVideosCardProps) => {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-primary/60">Total Videos</h3>
        {logo ? (
          <img src={logo} alt="App logo" className="h-6 w-6 rounded-full" />
        ) : (
          <FileVideo className="h-5 w-5 text-primary/60" />
        )}
      </div>
      <div className="mt-2">
        <p className="text-3xl font-semibold text-primary">{publishedCount}</p>
        <p className="text-sm text-primary/60">published</p>
      </div>
    </Card>
  );
};

interface TotalVideosCardProps {
  publishedCount: number;
  videoStatusCounts: {
    draft: number;
    review: number;
    approved: number;
    needs_revision: number;
  };
  logo?: string;
  onUnpublishedClick: () => void;
}

export const TotalVideosCard = ({
  publishedCount,
  videoStatusCounts,
  logo,
  onUnpublishedClick,
}: TotalVideosCardProps) => {
  const totalUnpublished = Object.values(videoStatusCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const totalVideos = publishedCount + totalUnpublished;

  const formatStatusLabel = (status: string) => {
    switch (status) {
      case "draft":
        return "Drafts";
      case "ai_reviewing":
      case "team_reviewing":
      case "brand_reviewing":
        return "In Review";
      case "approved":
      case "ai_approved":
        return "Approved";
      case "rejected":
      case "ai_rejected":
      case "team_rejected":
        return "Rejected";
      default:
        return status;
    }
  };

  // Calculate main categories
  const draftCount = videoStatusCounts.draft;
  const pendingCount =
    videoStatusCounts.review +
    videoStatusCounts.approved +
    videoStatusCounts.needs_revision;

  // Calculate percentages for the 3-category stacked bar
  const getMainStatusData = () => {
    if (totalVideos === 0) return [];

    const statuses = [
      {
        key: "published",
        count: publishedCount,
        label: "Published",
        color: "bg-blue-500",
      },
      {
        key: "draft",
        count: draftCount,
        label: "In Draft",
        color: "bg-slate-400",
      },
      {
        key: "pending",
        count: pendingCount,
        label: "Pending",
        color: "bg-amber-500",
      },
    ];

    return statuses
      .filter((status) => status.count > 0)
      .map((status) => ({
        ...status,
        percentage: (status.count / totalVideos) * 100,
      }));
  };

  const mainStatusData = getMainStatusData();

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-primary/60">
          Videos:{" "}
          <span className="font-semibold text-primary">{totalVideos}</span>{" "}
          total in project
        </h3>
        {logo ? (
          <img src={logo} alt="App logo" className="h-6 w-6 rounded-full" />
        ) : (
          <FileVideo className="h-5 w-5 text-primary/60" />
        )}
      </div>

      {/* Main Status Categories */}
      <div className="mt-4 space-y-3">
        {/* Main categories display */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-semibold text-blue-600">
              {publishedCount}
            </div>
            <div className="text-xs text-primary/60">Published</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-slate-600">
              {draftCount}
            </div>
            <div className="text-xs text-primary/60">In Draft</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-amber-600">
              {pendingCount}
            </div>
            <div className="text-xs text-primary/60">Pending</div>
          </div>
        </div>

        {/* Horizontal Stacked Bar for main categories */}
        {totalVideos > 0 && (
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            {mainStatusData.map((status) => (
              <div
                key={status.key}
                className={`${status.color} transition-all duration-300`}
                style={{ width: `${status.percentage}%` }}
                title={`${status.label}: ${status.count} (${status.percentage.toFixed(1)}%)`}
              />
            ))}
          </div>
        )}

        {/* Detailed clickable badges */}
        {totalUnpublished > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {Object.entries(videoStatusCounts)
              .filter(([_, count]) => count > 0)
              .map(([status, count]) => {
                const getButtonColor = (status: string) => {
                  switch (status) {
                    case "draft":
                      return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
                    case "ai_reviewing":
                    case "team_reviewing":
                    case "brand_reviewing":
                      return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800";
                    case "approved":
                    case "ai_approved":
                      return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800";
                    case "rejected":
                    case "ai_rejected":
                    case "team_rejected":
                      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800";
                    default:
                      return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
                  }
                };

                return (
                  <button
                    key={status}
                    onClick={onUnpublishedClick}
                    className={`
                      inline-flex items-center justify-between px-3 py-2
                      rounded-lg border text-xs font-medium
                      transition-all duration-200 ease-in-out
                      hover:scale-105 hover:shadow-sm
                      ${getButtonColor(status)}
                    `}
                  >
                    <span className="truncate">
                      {formatStatusLabel(status)}
                    </span>
                    <span className="ml-2 font-semibold">{count}</span>
                  </button>
                );
              })}
          </div>
        )}
      </div>
    </Card>
  );
};
